import { routes } from '@communities/routes';
import EmptyImage from '@core/assets/Empty.png';
import { ChevronRight } from '@soteria/design-system-ve';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CommunityImage } from './CommunityImage';
import { GroupMenu } from './GroupMenu';
import * as styles from './Guide.css';
import * as emptyStyles from './GuideEmptyState.css';
import type { CommunityGroupWithCommunities, GuideCommunity } from './useCommunityGroupsWithCommunities';

interface GuideGroupsListProps {
  communityGroups: CommunityGroupWithCommunities[];
  selectedCommunityId?: string | null;
  selectedGroupId?: string;
  isSystemAdmin: boolean;
  onCommunitySelect: (community: GuideCommunity) => void;
  onClose: () => void;
}

/**
 * GuideGroupsList component
 * Displays community groups with expandable communities in a drill-down pattern
 * Xbox-inspired compact navigation with optimized data loading
 */
export function GuideGroupsList({ communityGroups, selectedCommunityId, selectedGroupId, isSystemAdmin, onCommunitySelect, onClose }: GuideGroupsListProps) {
  const navigate = useNavigate();
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [openMenuGroupId, setOpenMenuGroupId] = useState<string | null>(null);

  // Memoize sorted communities for all groups
  const sortedCommunitiesMap = useMemo(() => {
    const map = new Map<string, GuideCommunity[]>();

    communityGroups.forEach(group => {
      const sorted = [...group.communities].sort((a, b) => a.name.localeCompare(b.name));

      map.set(group.id, sorted);
    });

    return map;
  }, [communityGroups]);

  // Automatically expand the group containing the selected community OR the selected group
  useEffect(() => {
    if (communityGroups.length === 0) {
      return;
    }

    // If on a community group page, expand that group
    if (selectedGroupId) {
      setExpandedGroupIds(prev => {
        if (!prev.has(selectedGroupId)) {
          const newSet = new Set(prev);

          newSet.add(selectedGroupId);

          return newSet;
        }

        return prev;
      });

      return;
    }

    // If on a community page, expand the group containing that community
    if (selectedCommunityId) {
      const groupWithSelectedCommunity = communityGroups.find(group => group.communities.some(community => community.id === selectedCommunityId));

      if (groupWithSelectedCommunity) {
        setExpandedGroupIds(prev => {
          if (!prev.has(groupWithSelectedCommunity.id)) {
            const newSet = new Set(prev);

            newSet.add(groupWithSelectedCommunity.id);

            return newSet;
          }

          return prev;
        });
      }
    }
  }, [selectedCommunityId, selectedGroupId, communityGroups]);

  const handleGroupClick = (groupId: string) => {
    setExpandedGroupIds(prev => {
      const newSet = new Set(prev);

      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }

      return newSet;
    });
  };

  const handleCommunityClick = (community: GuideCommunity) => {
    onCommunitySelect(community);
  };

  const handleViewGroupDetails = (groupId: string) => {
    navigate(routes.communityGroups.detail(groupId));
    setOpenMenuGroupId(null);
    onClose();
  };

  const handleAddCommunity = (groupId: string) => {
    navigate(`${routes.communityGroups.detail(groupId)}?action=create-community`);
    setOpenMenuGroupId(null);
    onClose();
  };

  if (communityGroups.length === 0) {
    return (
      <div className={emptyStyles.emptyStateWithImage}>
        <img alt="No community groups" className={emptyStyles.emptyStateImage} src={EmptyImage} />
        <p className={emptyStyles.emptyStateText}>No community groups found</p>
      </div>
    );
  }

  return (
    <div className={styles.groupsList}>
      {communityGroups.map(group => {
        const isExpanded = expandedGroupIds.has(group.id);
        // Check if user can add communities to this group
        // User must be either a system admin OR an admin of this specific group
        const canAddCommunity = isSystemAdmin || group.userRole === 'Admin';
        const sortedCommunities = sortedCommunitiesMap.get(group.id) || [];

        return (
          <div key={group.id} className={styles.groupItem}>
            <div className={styles.groupHeader}>
              <button
                aria-expanded={isExpanded}
                aria-label={`${group.name} community group`}
                className={styles.groupButton}
                type="button"
                onClick={() => handleGroupClick(group.id)}
              >
                <h3 className={styles.groupButtonHeading}>{group.name}</h3>
                <ChevronRight
                  className={styles.chevronIcon}
                  style={{
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </button>

              <GroupMenu
                canAddCommunity={canAddCommunity}
                groupId={group.id}
                groupName={group.name}
                isOpen={openMenuGroupId === group.id}
                onAddCommunity={handleAddCommunity}
                onOpenChange={isOpen => {
                  setOpenMenuGroupId(isOpen ? group.id : null);
                }}
                onViewGroupDetails={handleViewGroupDetails}
              />
            </div>

            {isExpanded && (
              <div aria-label={`Communities in ${group.name}`} className={styles.communityList} role="group">
                {group.communities.length === 0 ? (
                  <div className={emptyStyles.emptyState}>No communities in this group</div>
                ) : (
                  sortedCommunities.map(community => {
                    const communityLabelId = `community-label-${community.id}`;

                    return (
                      <button
                        key={community.id}
                        aria-labelledby={communityLabelId}
                        className={styles.communityButton}
                        id={`community-button-${community.id}`}
                        type="button"
                        onClick={() => handleCommunityClick(community)}
                      >
                        <CommunityImage communityName={community.name} loading="lazy" logoUrl={community.logoUrl} />
                        <span className={styles.communityName} id={communityLabelId}>
                          {community.name}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
